import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";

import "./auth-page.css";

export function AuthBrandLink() {
  return (
    <Link
      to="/"
      className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
      aria-label="Zephix home"
    >
      <div className="auth-page-brand-mark w-8 h-8 rounded-lg flex items-center justify-center">
        <Zap className="w-5 h-5 text-white" aria-hidden="true" />
      </div>
      <span className="text-2xl font-bold text-slate-900 tracking-tight">ZEPHIX</span>
    </Link>
  );
}

export function AuthCard({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`auth-page-card py-8 px-4 sm:rounded-2xl sm:px-10 ${className}`.trim()}>
      {children}
    </div>
  );
}

type AuthPageShellProps = {
  children: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Extra content under the brand (e.g. success-only layouts omit title). */
  testId?: string;
};

/**
 * Logged-out auth layout: v2 atmosphere, ZEPHIX mark, centered floating card column.
 */
export function AuthPageShell({ children, title, subtitle, testId }: AuthPageShellProps) {
  return (
    <div
      className="auth-page min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8"
      data-testid={testId}
    >
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <AuthBrandLink />
        </div>
        {title ? (
          <h2 className="mt-6 text-center text-3xl font-bold text-slate-900">{title}</h2>
        ) : null}
        {subtitle ? <div className="mt-2 text-center text-sm text-slate-600">{subtitle}</div> : null}
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">{children}</div>
    </div>
  );
}

export const authFieldClass =
  "auth-page-input appearance-none block w-full px-3 py-2 border border-slate-300 rounded-md placeholder-slate-400 sm:text-sm";

export const authFieldWithIconClass =
  "auth-page-input appearance-none block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md placeholder-slate-400 sm:text-sm";

export const authPrimaryButtonClass =
  "auth-page-btn-primary w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-opacity";

export const authLinkClass = "auth-page-link font-medium hover:underline";
